import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { events } from "@/mocks/events";
import StatusBadge from "@/components/base/StatusBadge";
import HeatMeter from "@/components/base/HeatMeter";
import EventCard from "@/components/feature/EventCard";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import usePageMeta from "@/hooks/usePageMeta";
import { siteInfo } from "@/mocks/site";

export default function EventDetail() {
  const { id } = useParams<{ id: string }>();
  const event = events.find((e) => e.id === id);

  const related = useMemo(
    () =>
      event
        ? events
            .filter((e) => e.id !== event.id)
            .sort((a, b) => {
              const sameA = a.categorySlug === event.categorySlug ? 1 : 0;
              const sameB = b.categorySlug === event.categorySlug ? 1 : 0;
              if (sameA !== sameB) return sameB - sameA;
              return b.heat - a.heat;
            })
            .slice(0, 3)
        : [],
    [event]
  );

  const jsonLd = useMemo(() => {
    if (!event) return undefined;
    return [
      {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: event.title,
        description: event.summary,
        inLanguage: "zh-CN",
        datePublished: event.date,
        dateModified: event.updatedAt,
        articleSection: event.category,
        keywords: event.tags.join(","),
        publisher: { "@type": "Organization", name: siteInfo.name },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "/" },
          { "@type": "ListItem", position: 2, name: "事件库", item: "/events" },
          { "@type": "ListItem", position: 3, name: event.title, item: `/event/${event.id}` },
        ],
      },
    ];
  }, [event]);

  usePageMeta({
    title: event
      ? `${event.title}｜时间线与前因后果复盘 - 瓜线`
      : "事件不存在 - 瓜线",
    description: event
      ? `${event.summary} 瓜线按时间线梳理该事件的关键节点与相关人物，还原前因后果全程。`
      : "未找到该事件档案，可返回事件库查看其它热门吃瓜时间线。",
    keywords: event
      ? `${event.title},${event.tags.join(",")},热门吃瓜时间线,吃瓜复盘`
      : "热门吃瓜",
    path: `/event/${id ?? ""}`,
    jsonLd,
  });

  if (!event) {
    return (
      <section className="w-full px-4 md:px-8 py-20 bg-background-50">
        <div className="mx-auto flex max-w-md flex-col items-center text-center">
          <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-200 text-3xl text-foreground-400">
            <i className="ri-file-unknow-line" />
          </span>
          <h1 className="mt-5 font-heading text-xl font-bold text-foreground-950">
            这个事件档案不存在
          </h1>
          <p className="mt-2 text-sm text-foreground-500">
            可能是链接有误，或者事件已经合并整理。回到事件库看看其它正在升温的故事线吧。
          </p>
          <Link
            to="/events"
            className="mt-6 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors duration-200 hover:bg-primary-600 cursor-pointer"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line" />
            </span>
            返回事件库
          </Link>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="relative w-full overflow-hidden">
        <div className="absolute inset-0 w-full h-full">
          <img
            src={event.cover}
            alt={`${event.title} 事件封面`}
            title={`${event.title} - 热门吃瓜时间线`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/75" />
        </div>
        <div className="relative w-full px-4 md:px-8 pt-10 pb-12 md:pt-14 md:pb-16">
          <Breadcrumbs
            tone="dark"
            items={[
              { name: "首页", path: "/" },
              { name: "事件库", path: "/events" },
              { name: event.title },
            ]}
          />
          <div className="mt-6 max-w-3xl">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-primary-500 px-3 py-1 text-xs font-semibold text-background-50">
                {event.category}
              </span>
              <StatusBadge status={event.status} className="bg-background-50/95 backdrop-blur" />
              <span className="text-xs text-background-300">起始 {event.date}</span>
              <span className="text-xs text-background-300">最近更新 {event.updatedAt}</span>
            </div>
            <h1 className="mt-4 font-heading text-2xl md:text-4xl font-extrabold leading-tight text-background-50">
              {event.title}
            </h1>
            <p className="mt-4 text-sm md:text-base leading-relaxed text-background-200">
              {event.summary}
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-4">
              <HeatMeter heat={event.heat} className="!text-accent-300" />
              <span className="inline-flex items-center gap-1.5 text-xs text-background-300">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-eye-line" />
                </span>
                {event.views} 次阅读
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs text-background-300">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-time-line" />
                </span>
                {event.readingTime}读完
              </span>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-12 md:py-16 bg-background-50">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <article>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                事件背景
              </h2>
              <p className="mt-4 text-sm md:text-base leading-loose text-foreground-700">
                {event.background}
              </p>

              <h2 className="mt-12 font-heading text-xl md:text-2xl font-bold text-foreground-950">
                时间线梳理
              </h2>
              <p className="mt-3 text-sm text-foreground-500">
                以下节点按时间先后排列，标签说明了该节点在整条故事线中的作用。
              </p>

              <ol className="mt-6 relative border-l border-primary-200 pl-6 md:pl-8">
                {event.timeline.map((node, idx) => (
                  <li key={node.title} className="relative pb-8 last:pb-0">
                    <span className="absolute -left-[31px] md:-left-[39px] top-1.5 w-3.5 h-3.5 rounded-full border-2 border-background-50 bg-primary-500" />
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs font-semibold text-primary-600">{node.date}</span>
                      {node.tag && (
                        <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-xs text-secondary-800">
                          {node.tag}
                        </span>
                      )}
                      <span className="text-xs text-foreground-400">节点 {idx + 1}</span>
                    </div>
                    <h3 className="mt-2 font-heading text-base md:text-lg font-bold text-foreground-950">
                      {node.title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-600">{node.desc}</p>
                  </li>
                ))}
              </ol>

              <h2 className="mt-12 font-heading text-xl md:text-2xl font-bold text-foreground-950">
                前因后果复盘
              </h2>
              <div className="mt-4 rounded-lg border-l-4 border-primary-500 bg-primary-50 p-5">
                <p className="text-sm md:text-base leading-loose text-foreground-800">
                  {event.conclusion}
                </p>
              </div>

              <div className="mt-8 flex flex-wrap gap-2">
                {event.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/events?q=${encodeURIComponent(tag)}`}
                    className="rounded-md bg-background-100 px-3 py-1.5 text-xs text-foreground-600 transition-colors duration-200 hover:bg-secondary-100 hover:text-secondary-800 cursor-pointer"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>
            </article>
          </div>

          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-24 flex flex-col gap-5">
              <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                <h2 className="font-heading text-base font-bold text-foreground-950">
                  相关人物与角色
                </h2>
                <p className="mt-1.5 text-xs leading-relaxed text-foreground-500">
                  涉及未证实传闻的对象均使用代称，等有明确结论后再补充说明。
                </p>
                <ul className="mt-4 space-y-3">
                  {event.people.map((person) => (
                    <li key={person.name} className="flex items-center gap-3">
                      <span className="w-9 h-9 shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 text-sm">
                        <i className="ri-user-line" />
                      </span>
                      <div className="min-w-0">
                        <div className="truncate text-sm font-semibold text-foreground-900">
                          {person.name}
                        </div>
                        <div className="truncate text-xs text-foreground-500">{person.role}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="font-heading text-base font-bold text-foreground-950">分享与纠错</h2>
                <p className="mt-1.5 text-xs leading-relaxed text-foreground-500">
                  如果时间线有遗漏或表述有误，欢迎反馈，我们会核对后更新。
                </p>
                <Link
                  to="/about#contact"
                  className="mt-4 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-foreground-950 px-4 py-2 text-sm font-semibold text-background-50 transition-colors duration-200 hover:bg-primary-600 cursor-pointer"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-edit-2-line" />
                  </span>
                  提交纠错
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {related.length > 0 && (
        <section className="w-full px-4 md:px-8 py-12 md:py-16 bg-background-100">
          <div className="w-full">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
              你可能还想追
            </h2>
            <p className="mt-2 text-sm text-foreground-500">同领域或热度接近的事件档案。</p>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {related.map((e) => (
                <EventCard key={e.id} event={e} />
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}